import { IntlShape } from 'react-intl';
import {
  createNumberCell,
  createSubtitledHeaderCell,
  formatValueAsPositiveOrNegative,
} from 'components/table/DataTableUtil';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { MetricsChart } from 'components/MetricsChart';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { InterpretationValue } from 'models/measureInterpretation';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Attribute, Measure } from 'services/model/model.service';
import { LineChartConfig } from 'components/chart/data/LineChartConfig';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { getChartColor } from 'components/chart/data/ChartColors';
import { ChartDataComparisonType } from 'components/chart/data/ChartDataComparisonType';
import { calcGrowth } from 'utils/calc';
import { DataEntry, DataResult } from 'models/data';
import { ExtendedAttributeCode } from 'pages/trading/model';
import { FilterValue } from 'components/breadcrumb/breadcrumbModel';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { tbColors } from 'styles/colors';
import { CurrencyCell } from 'components/table/standardCells/CurrencyCell';
import { NumberCell } from 'components/table/standardCells/NumberCell';
import { PercentCell } from 'components/table/standardCells/PercentCell';

export interface DemandTableRow {
  ordGmvVal: number;
  ordQty: number;
  ordQtyWow: number;
  ordQtyWowPer: number;
  ordQtyWowWeekBefore: number;
  ordQtyGrowthWowThreeWeekAvg: number;
  allGrowth: number;
  catGrowth: number;
  relativeGrowthGap: number;
  data: number[][];
}

export const createChartConfig = (data: number[][]) => {
  const chartConfig = LineChartConfig.forCustomMeasure(
    'growth',
    'Growth',
    MeasureUnitCode.PERCENT,
    InterpretationValue.Positive,
    false
  );

  chartConfig.showTooltips = false;
  chartConfig.legendConfig = { hide: true };
  chartConfig.removeOuterSpacing = true;

  const itemChartDataArray = new ChartDataArray(
    ChartDataType.primary,
    '',
    data[0].map((growth, index) => new ChartData(`Week ${index + 1}`, growth))
  );
  itemChartDataArray.color = getChartColor(0, false);

  const merchantChartDataArray = new ChartDataArray(
    ChartDataType.breakdown,
    '',
    data[1].map((growth, index) => new ChartData(`Week ${index + 1}`, growth))
  );
  merchantChartDataArray.color = tbColors.secondary['700'];
  merchantChartDataArray.comparisonType = ChartDataComparisonType.comparison;
  merchantChartDataArray.dashed = true;

  chartConfig.data = [itemChartDataArray, merchantChartDataArray];

  return chartConfig;
};

const columnHelper = createColumnHelper<DemandTableRow>();

export const buildStandardColumns = (ordQty: Measure, intl: IntlShape) =>
  [
    columnHelper.accessor('ordGmvVal', {
      id: 'ordGmvVal',
      header: () => intl.formatMessage({ id: 'demand.columns.ordGmv7d' }),
      cell: info => <CurrencyCell value={info.getValue()} />,
    }),
    columnHelper.accessor('ordQty', {
      id: 'ordQty',
      header: () => intl.formatMessage({ id: 'demand.columns.ordItems7d' }),
      cell: info => <NumberCell value={info.getValue()} />,
    }),
    columnHelper.accessor('ordQtyWowPer', {
      id: 'ordQtyWowPer',
      header: () =>
        createSubtitledHeaderCell(intl.formatMessage({ id: 'demand.columns.ordQtyWow' }), ordQty.metric.name),
      meta: {
        sortTitleId: 'demand.columns.ordQtyWow',
      },
      cell: info => (
        <PercentCell
          value={info.getValue()}
          signDisplay="always"
          className={formatValueAsPositiveOrNegative(info.getValue())}
        />
      ),
    }),
    columnHelper.accessor('ordQtyGrowthWowThreeWeekAvg', {
      id: 'ordQtyGrowthWowThreeWeekAvg',
      header: () =>
        createSubtitledHeaderCell(intl.formatMessage({ id: 'demand.columns.ordQty3Wow' }), ordQty.metric.name),
      meta: {
        sortTitleId: 'demand.columns.ordQty3Wow',
      },
      cell: info => (
        <PercentCell
          signDisplay="always"
          value={info.getValue()}
          className={formatValueAsPositiveOrNegative(info.getValue())}
        />
      ),
    }),
    columnHelper.accessor('data', {
      size: 150,
      enableSorting: false,
      header: () =>
        createSubtitledHeaderCell(
          intl.formatMessage({ id: 'demand.category.graph.title' }),
          intl.formatMessage({ id: 'demand.category.graph.subtitle' })
        ),
      cell: info => (
        <MetricsChart
          renderer={new LineChartRenderer(createChartConfig(info.getValue()))}
          className="no-ticks my-0 h-[50px] w-[100%] relative bottom-[-20px] left-[-34px]"
        />
      ),
    }),
    columnHelper.accessor('relativeGrowthGap', {
      id: 'relativeGrowthGap',
      header: () =>
        createSubtitledHeaderCell(intl.formatMessage({ id: 'demand.columns.relativeGrowthGap' }), ordQty.metric.name),
      meta: {
        sortTitleId: 'demand.columns.relativeGrowthGap',
      },
      cell: info =>
        createNumberCell(
          `${intl.formatNumber(info.getValue() * 100, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            signDisplay: 'always',
          })} p.P.`,
          formatValueAsPositiveOrNegative(info.getValue())
        ),
    }),
  ] as ColumnDef<DemandTableRow>[];

const createMerchantData = (dataEntry: DataEntry, key: Attribute, ordGmvVal: Measure, ordQty: Measure) => {
  return {
    key: key.getValue(dataEntry.attributes),
    ordGmvVal: ordGmvVal.getValue(dataEntry.values),
    ordQty: ordQty.getValue(dataEntry.values),
  };
};

const createEntriesMap = <T,>(
  dataEntries: DataEntry[],
  keyAttribute: Attribute,
  ordGmvVal: Measure,
  ordQty: Measure,
  extractAttributes: (entry: DataEntry) => T
) => {
  const map = {} as Record<string, { attributes: T; ordGmvVal: number; ordQty: number }>;
  if (dataEntries) {
    dataEntries.forEach((entry: DataEntry) => {
      map[keyAttribute.getRawValue(entry.attributes)] = {
        attributes: extractAttributes(entry),
        ordGmvVal: ordGmvVal.getValue(entry.values),
        ordQty: ordQty.getValue(entry.values),
      };
    });
  }

  return map;
};

export interface DemandTableProps {
  merchantData?: DataResult;
  filters?: Record<ExtendedAttributeCode, FilterValue[]>;
  localFilterFunction?: (row: DemandTableRow) => boolean;
}

export const digestData = <T,>(
  merchantResult: DataResult | null | undefined,
  entriesResult: DataResult | null | undefined,
  key: Attribute,
  ordGmvVal: Measure,
  ordQty: Measure,
  extractAttributes: (entry: DataEntry) => T,
  setTableRows: (value: (DemandTableRow & T)[]) => void,
  localFilterFunction?: (row: DemandTableRow) => boolean
) => {
  if (merchantResult && entriesResult) {
    const data = { ...entriesResult, sets: { ...entriesResult.sets, ...merchantResult.sets } };
    const entriesWeekMaps: Record<string, { attributes: T; ordGmvVal: number; ordQty: number }>[] = [];
    const merchantData: { key: string; ordGmvVal: number; ordQty: number }[] = [];
    for (let i = 0; i <= 4; i++) {
      entriesWeekMaps.push(
        createEntriesMap(data.sets[`entry-${i.toString()}`], key, ordGmvVal, ordQty, extractAttributes)
      );
      const merchantDataEntry = data.sets[`mer-${i.toString()}`][0];
      const merchantDataWeek = createMerchantData(merchantDataEntry, key, ordGmvVal, ordQty);
      if (merchantDataWeek) {
        merchantData.push(merchantDataWeek);
      }
    }
    //  bring both arrays into correct chronological order
    merchantData.reverse();
    entriesWeekMaps.reverse();

    const entriesLastWeek = entriesWeekMaps[4];
    const entriesInitialWeek = entriesWeekMaps[0];

    const merchantInitialOrdNum = merchantData[0].ordQty;
    const merchantCurrentOrdNum = merchantData[4].ordQty;
    const allGrowth = calcGrowth(merchantCurrentOrdNum, merchantInitialOrdNum);

    const rows: (DemandTableRow & T)[] = [];

    const merchantGrowthsArray: number[] = [];
    for (let i = 1; i <= 4; i++) {
      const growth = calcGrowth(merchantData[i].ordQty, merchantData[0].ordQty);
      merchantGrowthsArray.push(growth);
    }

    for (const key in entriesLastWeek) {
      const ordGmvValLW = entriesLastWeek[key].ordGmvVal;

      const ordQtyLW = entriesLastWeek[key].ordQty;
      const ordQtyBW = entriesInitialWeek[key].ordQty;
      const ordQtyWow = ordQtyLW - entriesWeekMaps[3][key].ordQty;
      const ordQtyWoWPer = calcGrowth(ordQtyLW, entriesWeekMaps[3][key].ordQty);
      const catGrowth = calcGrowth(entriesLastWeek[key].ordQty, ordQtyBW);
      const relativeGrowthGap = catGrowth - allGrowth;

      const checkAllWeeksHaveOrders = () => {
        return (
          entriesWeekMaps[0][key].ordQty > 0 &&
          entriesWeekMaps[1][key].ordQty > 0 &&
          entriesWeekMaps[2][key].ordQty > 0 &&
          entriesWeekMaps[3][key].ordQty > 0 &&
          entriesWeekMaps[4][key].ordQty > 0
        );
      };

      if (checkAllWeeksHaveOrders()) {
        const entriesGrowthsArray: number[] = [];
        for (let i = 1; i <= 4; i++) {
          const growth = calcGrowth(entriesWeekMaps[i][key].ordQty, ordQtyBW);
          entriesGrowthsArray.push(growth);
        }

        let avgItems = 0;
        for (let i = 1; i < 4; i++) {
          avgItems = avgItems + entriesWeekMaps[i][key].ordQty;
        }
        avgItems = avgItems / 3;

        rows.push({
          ...entriesLastWeek[key].attributes,
          ordGmvVal: ordGmvValLW,
          ordQty: ordQtyLW,
          ordQtyWow,
          ordQtyWowPer: ordQtyWoWPer,
          ordQtyWowWeekBefore: entriesWeekMaps[3][key].ordQty,
          ordQtyGrowthWowThreeWeekAvg: calcGrowth(ordQtyLW, avgItems),
          allGrowth,
          catGrowth,
          relativeGrowthGap,
          data: [entriesGrowthsArray, merchantGrowthsArray],
        });
      }
    }
    const sortedRows = rows.sort((a, b) => b.relativeGrowthGap - a.relativeGrowthGap);

    setTableRows(localFilterFunction ? sortedRows.filter(localFilterFunction) : sortedRows);
  } else {
    setTableRows([]);
  }
};

export const createCellExportOptions = (exportName: string): DataTableExportConfigOptions => ({
  exportName: exportName,
  skippedColumns: ['artImageUrl', 'data'],
  customHeaderExporters: {
    ordQtyWowPer: intl => intl.formatMessage({ id: 'demand.columns.ordQtyWow' }),
    ordQtyGrowthWowThreeWeekAvg: intl => intl.formatMessage({ id: 'demand.columns.ordQty3Wow' }),
    relativeGrowthGap: intl => intl.formatMessage({ id: 'demand.columns.relativeGrowthGap' }),
  },
  customCellValueExporters: {
    ordGmvVal: (cellValue, intl) => {
      return intl.formatNumber(cellValue as number, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    ordQtyWowPer: (cellValue, intl) => {
      return intl.formatNumber(cellValue as number, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: 'percent',
      });
    },
    ordQtyGrowthWowThreeWeekAvg: (cellValue, intl) => {
      return intl.formatNumber(cellValue as number, {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    relativeGrowthGap: (cellValue, intl) => {
      return `${intl.formatNumber((cellValue as number) * 100, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} p.P.`;
    },
  },
});
